import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  Fab,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Power,
  VideoLibrary,
  Publish,
  Language,
  RecordVoiceOver,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { projectService } from '../services/api';
import { useProjects } from '../contexts/ProjectContext';

interface ProjectCardProps {
  project: any;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
}

const languageIcons: Record<string, string> = {
  en: '🇺🇸',
  it: '🇮🇹',
  de: '🇩🇪',
};

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onEdit,
  onDelete,
  onToggleActive,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const progress = project.total_reels > 0 
    ? (project.published_reels / project.total_reels) * 100 
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'visible',
          '&:hover': {
            '& .project-actions': {
              opacity: 1,
            },
          },
        }}
        onClick={() => navigate(`/projects/${project.id}`)}
      >
        {!project.is_active && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            <Chip label="INACTIVE" color="error" />
          </Box>
        )}

        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {project.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {project.description || 'No description'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              className="project-actions"
              sx={{ opacity: 0, transition: 'opacity 0.2s' }}
            >
              <MoreVert />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={<Language />}
              label={`${languageIcons[project.language]} ${project.language.toUpperCase()}`}
              size="small"
              variant="outlined"
            />
            {project.default_voice && (
              <Chip
                icon={<RecordVoiceOver />}
                label={project.voice_label || project.default_voice}
                size="small"
                variant="outlined"
              />
            )}
            {project.auto_post && (
              <Chip
                label="Auto-Post"
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {project.published_reels}/{project.total_reels} reels
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VideoLibrary sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {project.total_reels}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Publish sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {project.published_reels}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {new Date(project.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </CardContent>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate(`/projects/${project.id}`);
            }}
          >
            <Edit sx={{ mr: 1, fontSize: 20 }} />
            View Details
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onEdit();
            }}
          >
            <Edit sx={{ mr: 1, fontSize: 20 }} />
            Edit Settings
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onToggleActive();
            }}
          >
            <Power sx={{ mr: 1, fontSize: 20 }} />
            {project.is_active ? 'Deactivate' : 'Activate'}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onDelete();
            }}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1, fontSize: 20 }} />
            Delete
          </MenuItem>
        </Menu>
      </Card>
    </motion.div>
  );
};

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { projects, fetchProjects, deleteProject } = useProjects();
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; project: any | null }>({
    open: false,
    project: null,
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      await fetchProjects();
    } catch (error) {
      enqueueSnackbar('Failed to load projects', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (projectId: number) => {
    try {
      await projectService.toggleActive(projectId);
      await fetchProjects();
      enqueueSnackbar('Project status updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to update project status', { variant: 'error' });
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteDialog.project) return;

    try {
      await deleteProject(deleteDialog.project.id);
      enqueueSnackbar('Project deleted successfully', { variant: 'success' });
      setDeleteDialog({ open: false, project: null });
    } catch (error) {
      enqueueSnackbar('Failed to delete project', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Projects
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your reel generation projects
          </Typography>
        </Box>
      </Box>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <Card
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <VideoLibrary sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.5 }} />
          </Box>
          <Typography variant="h5" sx={{ mb: 1 }}>
            No projects yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first project to start generating amazing faceless reels
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate('/projects/new')}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            Create First Project
          </Button>
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {projects && Array.isArray(projects) ? projects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard
                  project={project}
                  onEdit={() => navigate(`/projects/${project.id}`)}
                  onDelete={() => setDeleteDialog({ open: true, project })}
                  onToggleActive={() => handleToggleActive(project.id)}
                />
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No projects available
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* FAB for new project */}
          <Tooltip title="Create new project">
            <Fab
              color="primary"
              aria-label="add"
              onClick={() => navigate('/projects/new')}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              <Add />
            </Fab>
          </Tooltip>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, project: null })}
      >
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteDialog.project?.name}"? 
            This will permanently delete all associated reels and data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, project: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteProject} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;